import React, { ReactNode, useContext, useMemo } from "react";
import {
  Application,
  ApplicationName,
  filterAssignableApplications,
  filterVisibleApplications,
  formatApplication,
  getApplicationById,
  getApplicationByName
} from "./Application";
import { useAxios } from "../../shared/technical/rest/AxiosProvider";
import { getAllApplications } from "./ApplicationsApi";
import { ApplicationRole, formatApplicationRole } from "../users/ApplicationRoles";
import { useQuery } from "react-query";
import { CenteredCircularProgress } from "../../shared/components/progress/CenteredCircularProgress";
import { useAccount } from "../users/AccountProvider";
import { fullPageable, pageableWithSort } from "../../shared/technical/rest/pageable";

const currentApplicationContext = React.createContext<{
  applications: Application[],
  refresh: () => void,
} | undefined>(undefined);

/**
 * Gets all applications.
 */
export function useApplications() {
  let applicationContext = useContext(currentApplicationContext);
  if (!applicationContext) {
    throw new Error('No application context available! Probably not called inside CurrentApplicationContext.Provider')
  }
  return applicationContext.applications;
}

/**
 * Gets all applications that the actual user may see.
 * This will filter out applications for APPLICATION_ADMINs which they are no admin for
 */
export function useVisibleApplications() {
  const account = useAccount();
  const allApplications = useApplications();

  return useMemo(() => filterVisibleApplications(allApplications, account), [ allApplications, account ]);
}

/**
 * Gets all applications that the actual user may assign to other users.
 */
export function useAssignableApplications() {
  const account = useAccount();
  const allApplications = useApplications();
  return useMemo(() => filterAssignableApplications(allApplications, account), [ allApplications, account ]);
}

export type ApplicationFormatter = {
  formatApplication: (applicationName: ApplicationName) => string,
  formatApplicationRole: (applicationRole: ApplicationRole) => string
}

export function useApplicationFormatter(): ApplicationFormatter {
  const allApplications = useApplications();

  return useMemo(() => {
    return {
      formatApplication: (applicationName: ApplicationName) => {
        const application = getApplicationByName(allApplications, applicationName);
        if (!application) {
          return applicationName
        }
        return formatApplication(application)
      },
      formatApplicationRole: (applicationRole: ApplicationRole) => {
        let application = getApplicationByName(allApplications, applicationRole.application.name);
        if (!application && applicationRole.application.id) {
          application = getApplicationById(allApplications, applicationRole.application.id)
        }
        if (!application) {
          console.warn('Could not find application ' + JSON.stringify(applicationRole))
          return applicationRole.role
        }
        return formatApplicationRole(application, applicationRole.role)
      }
    }
  }, [ allApplications ])
}

export function ApplicationsProvider(props: {
  children: ReactNode
}) {
  const axios = useAxios();
  const sortByNamePageable = pageableWithSort(fullPageable(), { field: "name", order: "asc" })
  const { data: applications, refetch } = useQuery(APPLICATIONS_QUERY_KEY, () => getAllApplications(axios, sortByNamePageable));

  if (!applications) {
    return <CenteredCircularProgress loadingText={"Loading applications"} />
  }

  return (
    <currentApplicationContext.Provider value={{ applications: applications, refresh: refetch }}>
      {props.children}
    </currentApplicationContext.Provider>
  );
}

export const APPLICATIONS_QUERY_KEY = 'applications';
